/**
 * Fuente de verdad del contenido comercial de la landing (versión LATAM).
 *
 * REGLA: no se inventan datos del producto. Todo dato que todavía no fue
 * provisto queda como PLACEHOLDER entre corchetes para completarlo después.
 */

export interface Tomo {
  num: string
  name: string
  /** Cantidad de recetas del tomo (placeholder si no fue provista). */
  count: string
  desc: string
}
export interface RecipeAttr {
  title: string
  desc: string
  /** Marca el atributo destacado (Keto + Sin Gluten a la vez). */
  highlight?: boolean
}
export interface InteriorFeature {
  title: string
  desc: string
}
export interface AudienceItem {
  title: string
  desc: string
}
export interface Bono {
  num: string
  name: string
  /** Para qué sirve dentro de la colección. */
  role: string
  desc: string
  cover?: string
}
export interface Faq {
  q: string
  a: string
}

/** Nombre comercial único del producto. */
export const PRODUCT_NAME = '500+ Recetas Keto + Sin Gluten'

/**
 * Precio definitivo. Formato monetario único de la landing: siempre "USD 00".
 * USD 47 = precio regular (tachado). USD 17 = precio final que paga el cliente.
 */
export const PRICE = {
  previous: 'USD 47',
  current: 'USD 17',
  discount: '64% OFF',
  savings: 'Ahorras USD 30',
  note: 'Precio en dólares (USD) · Pago único',
} as const

/** PLACEHOLDER — completar con la política de garantía real (si existe). */
export const GUARANTEE_PLACEHOLDER = '[COMPLETAR: garantía / política de devolución]'

/** PLACEHOLDER — completar con los medios de pago habilitados. */
export const PAYMENT_PLACEHOLDER = '[COMPLETAR: medios de pago]'

export const PRODUCT_FACTS: string[] = [
  '+500 recetas',
  '6 tomos digitales',
  'Keto + Sin Gluten',
  '+4 bonos',
]

/** Hoy → Con la colección (única sección de problema/transformación). */
export const TRANSFORM_BEFORE: string[] = [
  'Abres la nevera y no sabes qué cocinar.',
  'Comes siempre los mismos tres o cuatro platos.',
  'Las recetas que encuentras son keto, pero llevan harina.',
  'Piden ingredientes difíciles de conseguir.',
  'Te aburres y terminas saliéndote de tu alimentación.',
]

export const TRANSFORM_AFTER: string[] = [
  'Abres la colección y eliges entre más de 500 recetas.',
  'Tienes variedad para cada momento del día.',
  'Cada receta es Keto y Sin Gluten al mismo tiempo.',
  'Ingredientes que consigues en cualquier supermercado.',
  'Comes rico todos los días y sostienes tu alimentación.',
]

/** Los 6 tomos de la colección (nombres tal como figuran en las portadas). */
export const TOMOS: Tomo[] = [
  {
    num: '01',
    name: 'Desayunos y Meriendas',
    count: '85 recetas',
    desc: 'Para arrancar el día y para la tarde, sin harinas ni azúcar.',
  },
  {
    num: '02',
    name: 'Almuerzos',
    count: '[CANTIDAD] recetas',
    desc: 'Platos completos para resolver el mediodía sin complicarte.',
  },
  {
    num: '03',
    name: 'Cenas',
    count: '85 recetas',
    desc: 'Opciones para resolver la noche sin repetir siempre lo mismo.',
  },
  {
    num: '04',
    name: 'Snacks, Picadas y Comidas Rápidas',
    count: '[CANTIDAD] recetas',
    desc: 'Para el hambre entre comidas y para cuando tienes poco tiempo.',
  },
  {
    num: '05',
    name: 'Panes, Masas y Horneados Salados',
    count: '[CANTIDAD] recetas',
    desc: 'Panes, masas y horneados sin harinas con gluten.',
  },
  {
    num: '06',
    name: 'Salsas, Aderezos y Básicos',
    count: '[CANTIDAD] recetas',
    desc: 'Todo lo que acompaña: salsas, aderezos y preparaciones base.',
  },
]

/** Atributos que cumple cada receta (tomados de la tapa de la colección). */
export const RECIPE_ATTRS: RecipeAttr[] = [
  {
    title: 'Keto + Sin Gluten a la vez',
    desc: 'No tienes que elegir entre una cosa y la otra: cada receta cumple con las dos.',
    highlight: true,
  },
  { title: 'Bajas en carbohidratos', desc: 'Pensadas para no sacarte de tu alimentación.' },
  { title: 'Altas en grasas saludables', desc: 'Aguacate, aceite de oliva, frutos secos, huevo.' },
  { title: 'Nutritivas', desc: 'Platos completos, no soluciones de emergencia.' },
  { title: 'Listas en minutos', desc: 'Preparaciones simples para el día a día.' },
  {
    title: 'Ingredientes accesibles',
    desc: 'Fáciles de conseguir en Latinoamérica, en el súper de siempre.',
  },
]

/** Cómo se ve una receta por dentro. */
export const INTERIOR_FEATURES: InteriorFeature[] = [
  { title: 'Foto del plato', desc: 'Ves cómo queda antes de cocinarlo.' },
  { title: 'Ingredientes claros', desc: 'Lista corta, con cantidades fáciles de seguir.' },
  { title: 'Tiempo y porciones', desc: 'Sabes cuánto tarda y para cuántos rinde.' },
  { title: 'Paso a paso', desc: 'Instrucciones numeradas, en simple, sin tecnicismos.' },
]

/** Para quién es la colección. */
export const AUDIENCE: AudienceItem[] = [
  {
    title: 'Haces Keto y te aburriste',
    desc: 'Necesitas variedad real para no comer siempre lo mismo.',
  },
  {
    title: 'Tienes que comer Sin Gluten',
    desc: 'Buscas recetas seguras, sin harinas de trigo, avena ni cebada.',
  },
  {
    title: 'Cocinas para toda la familia',
    desc: 'Quieres platos que le gusten a todos, no un menú aparte.',
  },
  {
    title: 'Tienes poco tiempo',
    desc: 'Prefieres preparaciones simples y rápidas de resolver.',
  },
  {
    title: 'Recién empiezas',
    desc: 'Quieres algo ordenado que te diga qué cocinar en cada momento del día.',
  },
]

/** Los 4 bonos de regalo (nombres tal como figuran en el material del producto). */
export const BONOS: Bono[] = [
  {
    num: '01',
    name: 'Lista de Súper Alimentos Keto',
    role: 'Para saber qué comprar',
    desc: 'Los alimentos que conviene tener siempre en casa para cocinar sin pensarlo.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/290769a2-eaca-4c26-a353-7d2ed0ddec7d.jpg',
  },
  {
    num: '02',
    name: 'Plan Semanal de Comidas',
    role: 'Para no improvisar',
    desc: 'Tu semana organizada: qué cocinar cada día, sin decidir a último momento.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/c58752d6-d7de-4537-904b-24dfe0304af9.jpg',
  },
  {
    num: '03',
    name: 'Tablas de Equivalencias y Conversiones',
    role: 'Para medir sin balanza',
    desc: 'Tazas, cucharadas y gramos: conviertes cualquier cantidad en segundos.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/f6ff44c4-839f-4598-bfc7-29c9dc699690.jpg',
  },
  {
    num: '04',
    name: 'Snacks Express',
    role: 'Para el hambre entre comidas',
    desc: 'Opciones rápidas para cuando quieres picar algo y no salirte del plan.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/eb982e33-7dd1-4ac6-8aaf-ccbb2168ace3.jpg',
  },
]

/** Contenido de la colección, dentro de la sección de oferta. */
export const OFFER_INCLUDES: string[] = [
  '6 tomos digitales con más de 500 recetas',
  'Todas las recetas Keto y Sin Gluten al mismo tiempo',
  'Ingredientes fáciles de conseguir en Latinoamérica',
  'Desayunos, meriendas, almuerzos, cenas, snacks, panes y básicos',
  'Cada receta con foto, ingredientes, tiempo, porciones y paso a paso',
  'Formato visual para leer desde el celular, la tablet o la computadora',
]

export const FAQS: Faq[] = [
  {
    q: '¿Las recetas son Keto y Sin Gluten al mismo tiempo?',
    a: 'Sí. Cada receta de los 6 tomos está pensada para cumplir con las dos cosas a la vez: baja en carbohidratos y sin harinas con gluten.',
  },
  {
    q: '¿Necesito ingredientes difíciles de conseguir?',
    a: 'No. La colección está pensada para Latinoamérica: los ingredientes se consiguen en supermercados y tiendas naturistas comunes.',
  },
  {
    q: '¿Sirve si recién empiezo con Keto?',
    a: 'Sí. Las recetas están explicadas paso a paso, en simple, y están ordenadas por momento del día para que sepas qué cocinar en cada comida.',
  },
  {
    q: '¿Son recetas complicadas?',
    a: 'No. Son preparaciones simples, con pocos pasos y tiempos cortos, pensadas para una cocina de casa.',
  },
  {
    q: '¿Cómo recibo la colección?',
    a: 'En formato digital, inmediatamente después de la compra. No se envía nada físico.',
  },
  {
    q: '¿Puedo verla desde el celular?',
    a: 'Sí. La abres desde el celular, la tablet o la computadora, y la tienes siempre a mano en la cocina.',
  },
  {
    q: '¿Es un pago único? ¿Qué medios de pago hay?',
    a: `Sí, es una compra única de ${PRICE.current} (precio regular ${PRICE.previous}), sin suscripciones ni pagos mensuales. Medios de pago: ${PAYMENT_PLACEHOLDER}.`,
  },
  {
    q: '¿Los 4 bonos están incluidos?',
    a: 'Sí. Los 4 bonos de regalo forman parte de la compra, sin costo adicional.',
  },
  {
    q: '¿Tiene garantía?',
    a: GUARANTEE_PLACEHOLDER,
  },
]
